/**
 * Upsell Products Integration
 * 
 * This script handles the integration of upsell products with the theme's
 * add to cart and dynamic checkout functionality.
 * 
 * Key features:
 * - Intercepts form submission to add main product + upsells together
 * - Handles both "Add to Cart" and "Buy Now" buttons
 * - Ensures proper cart state before redirecting
 * - Does not interfere with default theme AddToCartForm
 * - Re-initializes automatically when content is injected (e.g. QuickShopModal)
 */

(function() {
  'use strict';

  // Track initialized containers to prevent double-initialization
  var initializedContainers = new WeakSet();

  // Registry of per-container updateTotalPrice functions, called on main variant change
  var totalPriceUpdaters = [];

  // Global variant:change listener registered once
  document.addEventListener('variant:change', function() {
    totalPriceUpdaters.forEach(function(fn) { fn(); });
  });

  document.addEventListener('DOMContentLoaded', function() {
    // Initialize any upsell containers already in the DOM (product pages)
    document.querySelectorAll('.upsell-products').forEach(function(container) {
      initUpsellProducts(container);
    });

    // Watch for containers injected dynamically (QuickShopModal preloads content
    // on hover, before the modal is actually opened). This sets up event listeners
    // and price display, but we intentionally skip select sizing here because the
    // modal is still display:none and measurements would be unreliable.
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          var containers = [];
          if (node.classList && node.classList.contains('upsell-products')) {
            containers.push(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('.upsell-products').forEach(function(c) {
              containers.push(c);
            });
          }
          containers.forEach(function(c) { initUpsellProducts(c); });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // The theme fires 'modalOpen' after adding modal--is-active, at which point
    // the modal is laid out and measurements are accurate. Re-run select sizing
    // for any upsell selects inside the now-visible modal.
    document.addEventListener('modalOpen', function() {
      document.querySelectorAll('.modal--is-active .upsell-products [data-upsell-variant-select]').forEach(function(select) {
        resizeSelectToContent(select);
      });
    });
  });

  function initUpsellProducts(upsellContainer) {
    if (initializedContainers.has(upsellContainer)) return;
    initializedContainers.add(upsellContainer);

    const checkboxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]');
    const variantSelects = upsellContainer.querySelectorAll('[data-upsell-variant-select]');
    const titleLinks = upsellContainer.querySelectorAll('[data-upsell-title-link]');
    const totalPriceContainer = upsellContainer.querySelector('[data-upsell-total-container]');
    const totalPriceElement = upsellContainer.querySelector('[data-upsell-total-price]');

    const hideBuyNowButtons = upsellContainer.getAttribute('data-hide-buy-now-buttons') === 'true';

    let isProcessingSubmission = false;

    // Register this container's updateTotalPrice so the global variant:change listener can call it
    totalPriceUpdaters.push(updateTotalPrice);

    // Handle checkbox changes
    checkboxes.forEach(function(checkbox) {
      checkbox.addEventListener('change', function() {
        updateUpsellData();
        updateTotalPrice();
        if (hideBuyNowButtons) {
          updateBuyNowButtons();
        }
      });
    });

    // Prevent title links from toggling the checkbox via label click propagation
    titleLinks.forEach(function(link) {
      link.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    });

    // Handle variant selection changes
    variantSelects.forEach(function(select) {
      select.addEventListener('change', function() {
        updateVariantPrice(select);
        updateUpsellData();
        updateTotalPrice();
      });

      // Set correct initial price on load
      updateVariantPrice(select);

      // Size the select to its longest option text. On product pages the element
      // is visible immediately. In a QuickShopModal the element may be hidden
      // (preloaded before the modal opens) — the modalOpen listener handles that.
      resizeSelectToContent(select);
    });

    // Initialize buy now button state
    if (hideBuyNowButtons) {
      updateBuyNowButtons();
    }

    /**
     * Updates the price display for a specific variant selection
     */
    function updateVariantPrice(select) {
      const productId = select.getAttribute('data-product-id');
      const selectedOption = select.options[select.selectedIndex];
      const variantId = selectedOption.value;
      const price = selectedOption.getAttribute('data-price');
      const comparePrice = selectedOption.getAttribute('data-compare-price');

      // Sync the checkbox value to the selected variant
      const checkbox = upsellContainer.querySelector(`[data-upsell-checkbox][data-product-id="${productId}"]`);
      if (checkbox) {
        checkbox.value = variantId;
        checkbox.setAttribute('data-variant-id', variantId);
      }

      // Update regular price display
      const priceElement = upsellContainer.querySelector(`[data-upsell-price="${productId}"]`);
      if (priceElement && price) {
        if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
          priceElement.textContent = theme.Currency.formatMoney(parseInt(price), theme.settings.moneyFormat);
        } else {
          priceElement.textContent = '$' + (parseInt(price) / 100).toFixed(2);
        }
      }

      // Update compare-at price display
      const comparePriceElement = upsellContainer.querySelector(`[data-upsell-compare-price="${productId}"]`);
      if (comparePriceElement) {
        if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
          if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
            comparePriceElement.textContent = theme.Currency.formatMoney(parseInt(comparePrice), theme.settings.moneyFormat);
          } else {
            comparePriceElement.textContent = '$' + (parseInt(comparePrice) / 100).toFixed(2);
          }
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      }

      // Update the product title link to point to the selected variant
      const titleLink = upsellContainer.querySelector(`[data-upsell-title-link][data-product-id="${productId}"]`);
      if (titleLink) {
        const baseUrl = titleLink.getAttribute('href').split('?')[0];
        titleLink.setAttribute('href', baseUrl + '?variant=' + variantId);
      }
    }

    /**
     * Updates the global upsell data store
     */
    function updateUpsellData() {
      const checkedBoxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
      window.upsellItems = Array.from(checkedBoxes).map(function(checkbox) {
        return { id: checkbox.value, quantity: 1 };
      });
    }

    /**
     * Gets the current selected upsell items
     */
    function getSelectedUpsells() {
      const checkedBoxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
      return Array.from(checkedBoxes).map(function(checkbox) {
        return { id: checkbox.value, quantity: 1 };
      });
    }

    /**
     * Hide or show buy now buttons based on upsell selection
     */
    function updateBuyNowButtons() {
      const hasSelectedUpsells = getSelectedUpsells().length > 0;
      const buyNowButtons = document.querySelectorAll(
        '[data-shopify-payment-button], ' +
        '.shopify-payment-button, ' +
        '[data-dynamic-checkout-button], ' +
        '.dynamic-checkout__content, ' +
        '.shopify-payment-button__button, ' +
        'button[data-shopify="shopify_pay"]'
      );
      buyNowButtons.forEach(function(button) {
        if (hasSelectedUpsells) {
          button.style.display = 'none';
          button.setAttribute('data-upsell-hidden', 'true');
        } else if (button.getAttribute('data-upsell-hidden') === 'true') {
          button.style.display = '';
          button.removeAttribute('data-upsell-hidden');
        }
      });
    }

    /**
     * Calculate and update the total price (main product + selected upsells)
     */
    function updateTotalPrice() {
      if (!totalPriceContainer || !totalPriceElement) return;

      const selectedUpsells = getSelectedUpsells();

      if (selectedUpsells.length === 0) {
        totalPriceContainer.style.display = 'none';
        return;
      }

      // Scope form and price lookups to the same product section as this upsell block.
      // This ensures correctness inside the QuickShopModal as well as on full product pages.
      const productSection = upsellContainer.closest('[data-section-id]') || document.body;

      const productFormElement = productSection.querySelector('form[action*="/cart/add"]');
      if (!productFormElement) return;

      const mainVariantInput = productFormElement.querySelector('[name="id"]');
      if (!mainVariantInput) return;

      let mainProductPrice = 0;
      const priceElement = productSection.querySelector('[data-product-price]');
      if (priceElement) {
        const priceMatch = priceElement.textContent.trim().match(/[\d,]+\.?\d*/);
        if (priceMatch) {
          mainProductPrice = Math.round(parseFloat(priceMatch[0].replace(/,/g, '')) * 100);
        }
      }

      // Sum selected upsell prices
      let upsellsTotal = 0;
      selectedUpsells.forEach(function(upsell) {
        let price = 0;
        const variantOption = upsellContainer.querySelector('option[value="' + upsell.id + '"]');
        if (variantOption) {
          price = parseInt(variantOption.getAttribute('data-price')) || 0;
        } else {
          const checkbox = upsellContainer.querySelector('[data-upsell-checkbox][value="' + upsell.id + '"]');
          if (checkbox) {
            price = parseInt(checkbox.getAttribute('data-price')) || 0;
          }
        }
        upsellsTotal += price * upsell.quantity;
      });

      const grandTotal = mainProductPrice + upsellsTotal;

      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        totalPriceElement.textContent = theme.Currency.formatMoney(grandTotal, theme.settings.moneyFormat);
      } else {
        totalPriceElement.textContent = '$' + (grandTotal / 100).toFixed(2);
      }

      totalPriceContainer.style.display = 'flex';
    }

    /**
     * Clears all selected upsell checkboxes
     */
    function clearUpsellSelections() {
      upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked').forEach(function(checkbox) {
        checkbox.checked = false;
      });
    }

    /**
     * Main form submission handler
     * Intercepts the form submission BEFORE the theme's handler to add upsells
     */
    const productSection = upsellContainer.closest('[data-section-id]') || document.body;
    const productForm = productSection.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('submit', function(e) {
        const selectedUpsells = getSelectedUpsells();
        if (selectedUpsells.length > 0 && !isProcessingSubmission) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          isProcessingSubmission = true;

          const submitter = e.submitter;
          const isBuyNow = submitter && (
            submitter.classList.contains('shopify-payment-button__button') ||
            submitter.closest('.shopify-payment-button') !== null
          );

          addAllProductsToCart(productForm, selectedUpsells, isBuyNow)
            .finally(function() { isProcessingSubmission = false; });
        }
      }, true);
    }

    /**
     * Handles dynamic checkout button clicks
     */
    function setupDynamicCheckoutInterceptor() {
      const checkForPaymentButtons = setInterval(function() {
        const paymentButtons = document.querySelectorAll('.shopify-payment-button__button');
        if (paymentButtons.length > 0) {
          clearInterval(checkForPaymentButtons);
          paymentButtons.forEach(function(button) {
            button.addEventListener('click', function(e) {
              const selectedUpsells = getSelectedUpsells();
              if (selectedUpsells.length > 0 && !isProcessingSubmission) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                isProcessingSubmission = true;
                const form = button.closest('form');
                if (form) {
                  addAllProductsToCart(form, selectedUpsells, true)
                    .finally(function() { isProcessingSubmission = false; });
                }
              }
            }, true);
          });
        }
      }, 100);
      setTimeout(function() { clearInterval(checkForPaymentButtons); }, 5000);
    }

    setupDynamicCheckoutInterceptor();

    /**
     * Adds main product and upsells to cart together
     */
    function addAllProductsToCart(form, upsellItems, redirectToCheckout) {
      return new Promise(function(resolve, reject) {
        const formData = new FormData(form);
        const mainProductId = formData.get('id');
        const mainProductQuantity = parseInt(formData.get('quantity') || 1);

        if (!mainProductId) {
          console.error('No product variant selected');
          reject(new Error('No product variant selected'));
          return;
        }

        const allItems = [
          { id: mainProductId, quantity: mainProductQuantity },
          ...upsellItems
        ];

        const cartFormData = new FormData();
        allItems.forEach(function(item, index) {
          cartFormData.append('items[' + index + '][id]', item.id);
          cartFormData.append('items[' + index + '][quantity]', item.quantity);
        });

        fetch('/cart/add.js', {
          method: 'POST',
          body: cartFormData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(function(response) {
          if (!response.ok) {
            return response.json().then(function(err) {
              throw new Error(err.description || 'Failed to add items to cart');
            });
          }
          return response.json();
        })
        .then(function(data) {
          clearUpsellSelections();
          updateTotalPrice();

          if (redirectToCheckout) {
            window.location.href = '/checkout';
          } else {
            if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
              window.location.href = '/cart';
            } else {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              if (window.theme && window.theme.cart) {
                document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                  detail: { product: data },
                  bubbles: true
                }));
              }
            }
          }

          resolve(data);
        })
        .catch(function(error) {
          isProcessingSubmission = false;
          reject(error);
        });
      });
    }
  }

  /**
   * Measures the longest option text and sets the select width explicitly.
   * The select must be inside a visible (laid-out) container for measurements
   * to be accurate. On product pages this is always true. In QuickShopModal
   * this is called from the 'modalOpen' listener, after the modal is active.
   */
  function resizeSelectToContent(select) {
    var tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';

    var computed = window.getComputedStyle(select);
    tempSpan.style.fontSize = computed.fontSize;
    tempSpan.style.fontFamily = computed.fontFamily;
    tempSpan.style.fontWeight = computed.fontWeight;
    tempSpan.style.letterSpacing = computed.letterSpacing;

    // Append to body so the span is always in a rendered context
    document.body.appendChild(tempSpan);

    var maxWidth = 0;
    for (var i = 0; i < select.options.length; i++) {
      tempSpan.textContent = select.options[i].text;
      if (tempSpan.offsetWidth > maxWidth) {
        maxWidth = tempSpan.offsetWidth;
      }
    }

    document.body.removeChild(tempSpan);

    // Only apply if we got a real measurement (> 0 means element was rendered)
    if (maxWidth > 0) {
      // Add extra space for the native dropdown arrow and inner padding
      select.style.width = (maxWidth + 40) + 'px';
    }
  }

})();
